import React, { useState, useEffect } from 'react';
import {
  Webhook, Loader2, AlertCircle, CheckCircle2, XCircle, Send,
  Save, Info, KeyRound, ArrowLeftRight
} from 'lucide-react';
import { participantEndpointsApi } from '../../services/iso20022Api';

/**
 * Participant callback endpoints — where the switch calls BACK into the
 * participant's own systems. One card per callback type (payment status,
 * inbound recall, account notifications, RtP presentment): configure the URL
 * and optional auth header, then send a switch-signed test callback to prove
 * the switch can reach you.
 */
export default function CallbackEndpoints() {
  const [endpoints, setEndpoints] = useState(null);
  const [error, setError]         = useState(null);

  const load = () => {
    participantEndpointsApi.list()
      .then(setEndpoints)
      .catch(e => setError(e.response?.data?.error || e.message));
  };
  useEffect(load, []);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Webhook className="w-6 h-6 text-primary-600" /> Callback endpoints
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          The URLs of <em>your</em> systems that the switch sends requests and callbacks to.
        </p>
      </div>

      {/* Explainer */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-primary-900 flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4" /> How callbacks work
        </h2>
        <p className="text-sm text-primary-900 mt-2">
          In a production IPS most exchanges are asynchronous: you send a message to the switch, and the
          outcome — or a brand-new request where <strong>you</strong> are the receiving side — arrives later
          as a signed XML POST to an endpoint you host. Register those endpoints here. Every callback is a
          switch-signed ISO 20022 BusinessMessage sent with <code className="bg-primary-100 px-1 rounded">Content-Type: application/xml</code>,
          an <code className="bg-primary-100 px-1 rounded">X-Callback-Type</code> header naming the callback, and your optional
          auth header. Respond with any 2xx status to acknowledge. Delivered callbacks appear in the Message Ledger.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!endpoints && !error && (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading endpoints…
        </div>
      )}

      {endpoints && endpoints.map(ep => (
        <EndpointCard key={ep.key} endpoint={ep} onSaved={load} />
      ))}
    </div>
  );
}

function EndpointCard({ endpoint, onSaved }) {
  const [url, setUrl]                       = useState(endpoint.url || '');
  const [authHeaderName, setAuthHeaderName]   = useState(endpoint.authHeaderName || '');
  const [authHeaderValue, setAuthHeaderValue] = useState(endpoint.authHeaderValue || '');
  const [enabled, setEnabled]               = useState(endpoint.enabled);
  const [saving, setSaving]                 = useState(false);
  const [saveMsg, setSaveMsg]               = useState(null);   // { ok, text }
  const [testing, setTesting]               = useState(false);
  const [testResult, setTestResult]         = useState(null);   // API TestResult

  const dirty = url !== (endpoint.url || '')
    || authHeaderName !== (endpoint.authHeaderName || '')
    || authHeaderValue !== (endpoint.authHeaderValue || '')
    || enabled !== endpoint.enabled;

  const save = async () => {
    setSaving(true); setSaveMsg(null); setTestResult(null);
    try {
      await participantEndpointsApi.save(endpoint.key, { url, authHeaderName, authHeaderValue, enabled });
      setSaveMsg({ ok: true, text: 'Saved' });
      onSaved();
    } catch (e) {
      setSaveMsg({ ok: false, text: e.response?.data?.error || e.message });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true); setTestResult(null); setSaveMsg(null);
    try {
      setTestResult(await participantEndpointsApi.test(endpoint.key));
    } catch (e) {
      setTestResult({ success: false, detail: e.response?.data?.error || e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      {/* Title row */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{endpoint.name}</h3>
          <p className="text-sm text-gray-600 mt-1">{endpoint.description}</p>
          <p className="text-xs text-gray-500 mt-2">
            Delivers:{' '}
            {endpoint.messageTypes.map(t => (
              <code key={t} className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded mr-1">{t}</code>
            ))}
          </p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
          endpoint.configured
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-gray-50 text-gray-500 border border-gray-200'
        }`}>
          {endpoint.configured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Info className="w-3.5 h-3.5" />}
          {endpoint.configured ? 'Configured' : 'Not configured'}
        </span>
      </div>

      {/* URL */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Callback URL</label>
        <input
          type="url"
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https://your-cbs.example.com/iso20022/callbacks"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono
                     focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Auth header */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
            <KeyRound className="w-3.5 h-3.5" /> Auth header name <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={authHeaderName}
            onChange={e => setAuthHeaderName(e.target.value)}
            placeholder="X-API-Key"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Auth header value <span className="text-gray-400">(sent on every callback)</span>
          </label>
          <input
            type="password"
            value={authHeaderValue}
            onChange={e => setAuthHeaderValue(e.target.value)}
            placeholder="secret your system expects"
            autoComplete="new-password"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Enabled
        </label>

        <div className="grow" />

        <button
          onClick={save}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg
                     bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>

        <button
          onClick={sendTest}
          disabled={testing || !endpoint.configured || dirty}
          title={dirty ? 'Save your changes first' : !endpoint.configured ? 'Configure and save a URL first' : undefined}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg
                     border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send test callback
        </button>
      </div>

      {/* Feedback */}
      {saveMsg && (
        <p className={`text-sm flex items-center gap-1.5 ${saveMsg.ok ? 'text-green-700' : 'text-red-700'}`}>
          {saveMsg.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {saveMsg.text}
        </p>
      )}

      {testResult && (
        <div className={`rounded-lg border p-3 text-sm ${
          testResult.success ? 'bg-green-50 border-green-200 text-green-800'
                             : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <p className="font-medium flex items-center gap-1.5">
            {testResult.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {testResult.success ? 'Test callback delivered' : 'Test callback failed'}
          </p>
          <p className="mt-1">
            {testResult.messageType && (
              <>Sent a switch-signed <code className="bg-white/60 px-1 rounded">{testResult.messageType}</code>{' '}
              {testResult.url && <>to <code className="bg-white/60 px-1 rounded break-all">{testResult.url}</code></>}
              {typeof testResult.durationMs === 'number' && <> in {testResult.durationMs}ms</>}
              {' — '}</>
            )}
            {testResult.detail}
          </p>
        </div>
      )}
    </div>
  );
}
